import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import Icon from '../../components/AppIcon';
import PlayCard from './components/PlayCard';

const StudentPlaysLibrary = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [plays, setPlays] = useState([]);
  const [filteredPlays, setFilteredPlays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPublishedPlays();
  }, []);

  useEffect(() => {
    filterPlays();
  }, [searchQuery, selectedDifficulty, plays]);

  const fetchPublishedPlays = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase?.from('plays')?.select('*')?.eq('is_published', true)?.order('title', { ascending: true });

      if (fetchError) throw fetchError;

      setPlays(data || []);
    } catch (err) {
      console.error('Error fetching plays:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const filterPlays = () => {
    let filtered = [...plays];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered?.filter(
        (play) =>
          play?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          play?.author?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          play?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Filter by difficulty
    if (selectedDifficulty !== 'all') {
      filtered = filtered?.filter((play) => play?.difficulty === selectedDifficulty);
    }

    setFilteredPlays(filtered);
  };

  const handlePlaySelect = (playId) => {
    navigate(`/play-reading-interface?playId=${playId}`);
  };

  const getDifficultyBadgeColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'hard':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <>
      <Helmet>
        <title>Plays Library - BardPlay</title>
        <meta 
          name="description" 
          content="Explore Shakespeare's plays and begin your literary journey with BardPlay's interactive reading experience" 
        />
      </Helmet>
      <RoleBasedNavigation />
      <main className="min-h-screen bg-gradient-to-br from-surface-dark via-[#1a0033] to-surface-dark pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Icon name="Theater" size={40} className="text-accent-purple" />
              <h1 className="text-4xl font-bold bg-gradient-to-r from-accent-purple to-accent-blue bg-clip-text text-transparent">
                Shakespeare's Plays
              </h1>
            </div>
            <p className="text-text-secondary text-lg">
              Select a play to begin your interactive reading journey through the works of William Shakespeare
            </p>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-surface-lighter border border-border rounded-xl p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search Input */}
              <div className="relative">
                <Icon 
                  name="Search" 
                  size={20} 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" 
                />
                <input
                  type="text"
                  placeholder="Search plays by title, author, or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-12 pr-4 py-3 bg-surface-dark border border-border rounded-lg text-white placeholder-text-secondary focus:outline-none focus:border-accent-purple transition-colors"
                />
              </div>

              {/* Difficulty Filter */}
              <div className="relative">
                <Icon 
                  name="Filter" 
                  size={20} 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" 
                />
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e?.target?.value)}
                  className="w-full pl-12 pr-4 py-3 bg-surface-dark border border-border rounded-lg text-white focus:outline-none focus:border-accent-purple transition-colors appearance-none cursor-pointer"
                >
                  <option value="all">All Difficulties</option>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
                <Icon 
                  name="ChevronDown" 
                  size={20} 
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" 
                />
              </div>
            </div>

            {/* Active Filters Display */}
            {(searchQuery || selectedDifficulty !== 'all') && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                <span className="text-sm text-text-secondary">Active filters:</span>
                {searchQuery && (
                  <span className="px-3 py-1 bg-accent-purple/20 text-accent-purple rounded-full text-sm flex items-center gap-2">
                    Search: "{searchQuery}"
                    <button onClick={() => setSearchQuery('')} className="hover:text-white">
                      <Icon name="X" size={14} />
                    </button>
                  </span>
                )}
                {selectedDifficulty !== 'all' && (
                  <span className="px-3 py-1 bg-accent-purple/20 text-accent-purple rounded-full text-sm flex items-center gap-2 capitalize">
                    {selectedDifficulty}
                    <button onClick={() => setSelectedDifficulty('all')} className="hover:text-white">
                      <Icon name="X" size={14} />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-8 flex items-start gap-3">
              <Icon name="AlertCircle" size={24} className="text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-red-400 font-semibold mb-1">Error Loading Plays</h3>
                <p className="text-red-300/80 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)]?.map((_, index) => (
                <div 
                  key={index} 
                  className="bg-surface-lighter border border-border rounded-xl h-80 animate-pulse"
                />
              ))}
            </div>
          )}

          {/* Plays Grid */}
          {!loading && !error && (
            <>
              {filteredPlays?.length === 0 ? (
                <div className="text-center py-16">
                  <Icon name="BookOpen" size={64} className="text-text-secondary mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Plays Found</h3>
                  <p className="text-text-secondary mb-6">
                    {searchQuery || selectedDifficulty !== 'all' ?'Try adjusting your filters to see more plays' :'No published plays are currently available'}
                  </p>
                  {(searchQuery || selectedDifficulty !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedDifficulty('all');
                      }}
                      className="btn-secondary"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-text-secondary">
                      Showing <span className="text-white font-semibold">{filteredPlays?.length}</span> play{filteredPlays?.length !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredPlays?.map((play) => (
                      <PlayCard
                        key={play?.id}
                        play={play}
                        onSelect={handlePlaySelect}
                        getDifficultyBadgeColor={getDifficultyBadgeColor}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default StudentPlaysLibrary;